import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { StageComponentInterface } from '../../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../../reduxToolkit/typedRedux';
import VyTooltip from '../../../../../common/VyTooltip';
import LikeButtonStageWrapper from '../../LikeButtonStageWrapper';
import { mbwCustomTemplateOrgId } from '../../../../../../static/constants';
import tinycolor from 'tinycolor2';

export interface Item {
  id: number;
  uid?: string;
  type: 'selfinfo' | 'companyinfo' | 'chat' | 'qa' | 'slido' | 'poll' | 'like' | 'speakers' | 'downloads' | 'agenda';
  icon?: string;
  element?: JSX.Element;
  customLabel?: string | null; // MBW Label based on the uicomponent title
  label: string;
  content?: JSX.Element;
  visible: boolean;
  defaultOpen: boolean;
  mustAuth: boolean;
  showBackArrow?: boolean;
  activeBckg?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  interactive?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

interface StageMenuInt {
  items: Item[];
  uiComponent?: StageComponentInterface;
  isClassicMenu?: boolean;
  videoHeight?: number;
  fullScreen?: boolean;
  setMenuItem?: (item: Item | undefined) => void;
  selectedMenuItem?: Item | undefined;
}

const MbwStageMenu = ({ items, uiComponent, isClassicMenu, videoHeight, fullScreen, setMenuItem, selectedMenuItem }: StageMenuInt) => {
  const user = useSelector((state) => state.common.user);
  const event = useSelector((state) => state.common.event);
  const polls = useSelector((state) => state.common.eventPoll?.filter((p) => p.isActive));
  const pagedata = useSelector((state) => state.common.pagedata);
  const isHybrid = useSelector((state) => state.common.isHybrid);

  const menuRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const [menuItems, setMenuItems] = useState(items);
  const [menuItemsHeight, setMenuItemsHeight] = useState(0);

  function openMenu(item: Item) {
    setMenuItem?.(selectedMenuItem?.id === item.id ? undefined : item);
  }

  function init() {
    const stageSettings = !isClassicMenu ? uiComponent?.stageSettings : pagedata?.stageSettings;
    if (!stageSettings) return;
    const finalMenuItems = items.map((item) => {
      switch (item.type) {
        case 'chat':
          item.visible = stageSettings?.chat.active === true;
          item.defaultOpen = stageSettings?.chat?.defaultOpen === true || (isHybrid === true && stageSettings?.chat.active === true);
          break;
        case 'qa':
          item.visible = stageSettings?.qa.active === true;
          item.defaultOpen = stageSettings?.qa.defaultOpen === true;
          break;
        case 'poll':
          item.visible = stageSettings?.poll?.active === true;
          item.defaultOpen = stageSettings?.poll?.defaultOpen === true;
          item.disabled = !user;
          item.disabledTooltip = t('event:poll-disabled-tooltip');
          break;
        case 'selfinfo':
          item.visible = stageSettings?.selfinfo.active === true;
          item.defaultOpen = stageSettings?.selfinfo.defaultOpen === true;
          break;
        case 'companyinfo':
          item.visible = stageSettings?.companyinfo.active === true;
          item.defaultOpen = stageSettings?.companyinfo.defaultOpen === true;
          break;
        case 'slido':
          item.visible = stageSettings?.slido.active === true;
          item.defaultOpen = stageSettings?.slido.defaultOpen === true;
          break;
        case 'like':
          item.visible = stageSettings?.like?.active === true;
          item.disabled = !user;
          item.disabledTooltip = t('event:like-disabled-tooltip');
          break;
        case 'speakers':
          item.visible = stageSettings?.speakers.active === true;
          break;
        case 'agenda':
          item.visible = stageSettings?.agenda?.active === true;
          break;
        case 'downloads':
          item.visible = stageSettings?.downloads.active === true;
          break;
        default:
          item.visible = true;
          break;
      }
      return item;
    });
    setMenuItems(finalMenuItems);
  }

  function determineItemColors(item: Item, selected: boolean) {
    let backgroundColor: string;
    let iconColor: string;

    if (event?.organization?._id === mbwCustomTemplateOrgId) {
      backgroundColor = highlight(item)
        ? '#ED7D32'
        : selected
        ? tinycolor(uiComponent?.customStyling?.buttonColor ?? event?.template?.mainColor)
            .darken(4)
            .toString()
        : uiComponent?.customStyling?.buttonColor ?? event?.template?.mainColor;
      iconColor = highlight(item) ? 'white' : uiComponent?.customStyling?.buttonTextColor ?? event?.template?.mainButtonTextColor;
    } else {
      backgroundColor = highlight(item) ? '#ED7D32' : selected ? tinycolor('rgba(0, 40, 80, 0.6)').darken(5).toString() : 'rgba(0, 40, 80, 0.6)';
      iconColor = 'white';
    }

    return { backgroundColor, iconColor };
  }

  useEffect(() => {
    if (isItemSelected || !videoHeight) return;
    setMenuItemsHeight(videoHeight);
  }, [videoHeight]);

  useEffect(() => {
    // in case selected item gets updated
    if (selectedMenuItem) setMenuItem?.(menuItems.find((item) => item.id === selectedMenuItem.id));
    menuItems.some((item) => {
      if (item.defaultOpen === true) {
        setMenuItem?.(item);
        return true;
      }
      return false;
    });
  }, [menuItems]);

  useEffect(() => {
    init();
  }, [uiComponent, pagedata]);

  const visibleItems = menuItems.filter((item) => item.visible === true);

  const isItemSelected = selectedMenuItem && selectedMenuItem.type !== 'like';
  const longSeparatorIndex = visibleItems.findIndex((item) => item.interactive) - 1;
  const highlight = (item: Item) => !item.disabled && item.type === 'poll' && !!polls && polls.length > 0;
  return (
    <div
      className="flex flex-col-reverse justify-end mt-4 menu-container md:flex-row md:justify-start md:mt-0"
      style={{ color: uiComponent?.styling.textColor ?? 'white' }}>
      <div
        style={{
          height: 'inherit',
          backgroundColor:
            event?.organization?._id === mbwCustomTemplateOrgId
              ? uiComponent?.customStyling?.sidePanelBackground ?? 'rgba(0, 102, 109, 1)'
              : 'rgba(0, 40, 80, 0.9)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE and Edge
        }}
        className={`section-container
        md:w-auto md:h-auto md:max-h-[35rem] max-w-full ml-0 md:ml-auto md:max-w-[40rem] overflow-scroll self-end md:self-auto ${
          menuRef?.current?.offsetHeight ? `md:min-h-[${menuRef.current?.offsetHeight}px]` : 'md:min-h-[400px]'
        }
        ${isItemSelected ? 'section-container--expanded border-2 border-white ml-4  pr-2' : ''} ${fullScreen === true ? 'bg-opacity-50' : 'bg-opacity-30'}`}>
        <div className="flex w-full h-full custom-scrollbar">{selectedMenuItem?.content}</div>
      </div>
      {visibleItems.length > 0 && (
        <div ref={menuRef} className="flex flex-row py-4 w-full md-horizontal-line md:flex-col md:py-0 md:h-full md:w-auto">
          {visibleItems.map((item, i) => {
            const selected = item.id === selectedMenuItem?.id;
            const isLikeItem = item.type === 'like';

            console.log(item?.icon);
            return (
              <div
                className={`flex relative flex-col justify-center ${
                  i !== visibleItems.length - 1 ? (i !== longSeparatorIndex ? 'w-auto sm:w-20 md:w-auto' : 'md:h-full w-full sm:pr-20 md:pr-0') : 'w-auto'
                } items-start md:items-center ${i === visibleItems.length - 1 ? 'flex-initial' : 'flex-1 md:flex-initial'}`}>
                <div className="absolute left-10 border-b border-white border-solid md-divider-horizontal sm:left-14" />
                <div className={`flex justify-center items-center relative ${!selected && 'md:pl-4'} `}>
                  {selected && !isLikeItem && <div className="relative divider-horizontal md-divider-vertical" />}
                  {isLikeItem && (
                    <div className={`${selected ? 'divider-horizontal' : ''} relative`}>
                      <LikeButtonStageWrapper theme="mbw" hideMenu={!selected} />
                    </div>
                  )}
                  <VyTooltip title={item.disabled ? item.disabledTooltip : item.customLabel ?? t(`pagebuilder:${item.label}`)}>
                    <div
                      style={{
                        backgroundColor: determineItemColors(item, selected).backgroundColor,
                      }}
                      className={`flex justify-center ${item.disabled ? '' : 'cursor-pointer'}
                      items-center h-14 w-14 lg:w-16 lg:h-16 
                      ${!highlight(item) && selected ? 'backdrop-blur-3xl' : 'bg-white bg-opacity-30 backdrop-blur-sm'} `}
                      onClick={() => {
                        !item.disabled && openMenu(item);
                      }}>
                      {item.icon && (
                        <div
                          style={{
                            width: isLikeItem ? '55%' : '40%',
                            height: 'auto',
                            aspectRatio: '1',
                            backgroundColor: item.disabled ? 'lightgray' : determineItemColors(item, selected).iconColor,
                            WebkitMask: `url(${item?.icon}) no-repeat center / contain`,
                            mask: `url(${item?.icon}) no-repeat center / contain`,
                          }}
                        />
                      )}
                    </div>
                  </VyTooltip>
                </div>
                {i < visibleItems.length - 1 && (
                  <div
                    style={{ minHeight: i === longSeparatorIndex || longSeparatorIndex === 0 ? '2vw' : 6 / (longSeparatorIndex + 1) + 'vw' }}
                    className={`hidden md:flex divider-vertical pl-4 ${i === longSeparatorIndex ? 'md:h-full' : 'md:h-4'}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
      <style jsx>
        {`
          .menu-container {
            max-height: ${menuItemsHeight ? menuItemsHeight + 'px' : '60vh'};
            min-height: ${menuItemsHeight > 450 ? menuItemsHeight : 400}px;
            .section-container {
              ${menuRef?.current?.offsetHeight ? `min-height:${menuRef.current?.offsetHeight}px;` : 'min-height: 400px;'}
              ::-webkit-scrollbar {
                display: none;
              }
            }
            .section-container--expanded {
              width: 50vw !important;
            }
            @media (max-width: 768px) {
              .section-container--expanded {
                width: 100% !important;
              }
              .md-divider-horizontal {
                border-bottom: 1px solid white;
                width: calc(100% - 3.5rem);
                height: 1px;
                bottom: 20px;
              }
              .md-divider-vertical {
                border-right: 1px solid white;
                height: 1rem;
                position: absolute;
                bottom: -1rem;
              }
            }
            @media (max-width: 640px) {
              .md-divider-horizontal {
                width: calc(100% - 40px);
              }
            }
            .divider-vertical {
              border-right: 1px solid white;
            }
            .icon {
              width: 0.6875em;
              height: 0.6875em;
            }

            @media (min-width: 768px) {
              .divider-horizontal {
                border-bottom: 1px solid white;
                width: 1rem;
                height: 1px;
              }
              .icon {
                width: 0.455em;
                height: 0.45em;
              }
            }

            .custom-scrollbar {
              overflow: auto;
              height: inherit;
            }
            @media (min-width: 768px) {
              .custom-scrollbar {
                overflow-y: auto;
                overflow-x: hidden;
              }
              .custom-scrollbar::-webkit-scrollbar {
                width: 12px;
              }

              .custom-scrollbar::-webkit-scrollbar-track {
                background: linear-gradient(#fff, #fff) no-repeat center/2px 98%;
              }
              .custom-scrollbar::-webkit-scrollbar-thumb {
                background: white;
                border: 5px solid white;
                border-radius: 15px;
              }
            }
          }
          @media (max-width: 768px) {
            .menu-container {
              height: auto;
              max-height: 35rem;
              min-height: 0;
            }
          }
        `}
      </style>
    </div>
  );
};

export default MbwStageMenu;
